import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type QueryClient } from '@tanstack/react-query';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';
import { useSaveFileMutation } from '#apps/client-web/features/file-storage/hooks/use-save-file-mutation';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { useDeleteFileMutation } from '#apps/client-web/features/file-storage/hooks/use-delete-file-mutation';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  AuditLightingTypeFormSchema,
  type AuditLightingTypeForm,
  type AuditLightingTypeFormEntry,
} from '#packages/data-collection/models/audit-lighting-type-form';
import { type Attachment } from '#packages/data-collection/models/common/attachment';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { useMenu } from '#packages/web/hooks/use-menu';

import { fromFormData, toFormData } from './mappers';
import {
  type EditAuditFormLightingTypeEntryData,
  EditAuditFormLightingTypeEntryDataSchema,
} from './types';

export type AuditFormLightingTypeEntryPageViewModel = ReturnType<
  typeof useAuditFormLightingTypeEntryPageViewModel
>;

type AuditFormLightingTypeEntryPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormLightingTypeEntryPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormLightingTypeEntryPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId, index: indexAsString } = useParams<{
    auditId: AuditId;
    index: string;
  }>();
  const index = parseInt(indexAsString!);

  const auditFormQuery = useLoadAuditFormByType<AuditLightingTypeForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'lighting-type',
    queryClient,
  });

  const saveFileStorageMutation = useSaveFileMutation({
    fileStorageService,
    queryClient,
  });

  const deleteFileStorageMutation = useDeleteFileMutation({
    fileStorageService,
    queryClient,
  });

  const fileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries[index]?.attachments ?? []).map(
        (item) => item.name,
      ),
    [auditFormQuery.data?.entries, index],
  );

  const fileStorageQuery = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditLightingTypeForm>(
    {
      auditFormService,
      queryClient,
      auditId: auditId!,
      formType: 'lighting-type',
    },
  );

  const form = useForm({
    resolver: zodResolver(
      EditAuditFormLightingTypeEntryDataSchema,
    ) as Resolver<EditAuditFormLightingTypeEntryData>,
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  const onSave = useCallback(
    (formData: EditAuditFormLightingTypeEntryData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update lighting entry, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating lighting form data');

      const newFileIds = (formData.attachments ?? []).map(
        (attachment) => attachment.name,
      );
      const deletedFileIds = fileIds.filter(
        (fileId) => !newFileIds.includes(fileId),
      );
      deletedFileIds.forEach((fileId) => {
        deleteFileStorageMutation.mutate({ fileId });
      });

      const attachments: Attachment[] | undefined = formData.attachments?.map(
        (attachment) => {
          saveFileStorageMutation.mutate({
            fileId: attachment.name,
            content: attachment.file,
          });
          return {
            name: attachment.name,
            mimeType: attachment.file.type,
            isSelectedForReport: attachment.isSelectedForReport,
          };
        },
      );

      const previousDataEncoded = auditFormQuery.data
        ? AuditLightingTypeFormSchema.encode(auditFormQuery.data)
        : { type: 'lighting-type' as const, entries: [] };

      const newEntryRaw = fromFormData(formData, attachments);

      if (index === -1) {
        previousDataEncoded.entries.push({
          ...newEntryRaw,
        });
      } else {
        previousDataEncoded.entries[index] = {
          ...newEntryRaw,
        };
      }

      const auditLightingForm =
        AuditLightingTypeFormSchema.decode(previousDataEncoded);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditLightingForm },
        {
          onSuccess: (savedForm: AuditLightingTypeForm) => {
            logger?.debug(
              { auditId, savedForm },
              'Lighting form updated successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('Lighting form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update lighting form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      index,
      auditId,
      saveFileStorageMutation.mutate,
      deleteFileStorageMutation.mutate,
      fileIds,
      auditFormQuery.data,
      AuditLightingTypeFormSchema.encode,
      AuditLightingTypeFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (
      (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) &&
      (saveFileStorageMutation.isIdle || saveFileStorageMutation.isSuccess) &&
      (deleteFileStorageMutation.isIdle || deleteFileStorageMutation.isSuccess)
    ) {
      return { state: 'idle' };
    }

    if (
      saveAuditFormMutation.isPending ||
      saveFileStorageMutation.isPending ||
      deleteFileStorageMutation.isPending
    ) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    if (saveFileStorageMutation.isError) {
      return { state: 'error', error: saveFileStorageMutation.error };
    }

    if (deleteFileStorageMutation.isError) {
      return { state: 'error', error: deleteFileStorageMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
    saveFileStorageMutation.isIdle,
    saveFileStorageMutation.isPending,
    saveFileStorageMutation.isSuccess,
    saveFileStorageMutation.isError,
    saveFileStorageMutation.error,
    deleteFileStorageMutation.isIdle,
    deleteFileStorageMutation.isPending,
    deleteFileStorageMutation.isSuccess,
    deleteFileStorageMutation.isError,
    deleteFileStorageMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{
      form: AuditLightingTypeFormEntry | undefined;
      files: (FileContent | undefined)[];
    }>
  >(() => {
    const areFilesLoaded =
      fileIds.length === 0 ||
      fileStorageQuery.every((query) => query.isSuccess);
    if (auditFormQuery.isSuccess && areFilesLoaded) {
      const files =
        fileStorageQuery.map((item) => item.data ?? undefined) ?? undefined;
      return {
        state: 'success',
        data: { form: auditFormQuery.data?.entries[index], files },
      };
    }

    const areFilesLoading =
      fileIds.length > 0 &&
      fileStorageQuery.some((query) => query.isLoading || query.isPending);
    if (
      auditFormQuery.isLoading ||
      auditFormQuery.isPending ||
      areFilesLoading
    ) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return { state: 'error', error: auditFormQuery.error };
    }

    const fileError = fileStorageQuery.find((query) => query.isError)?.error;
    if (!isNil(fileError)) {
      return { state: 'error', error: fileError };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    fileStorageQuery,
    fileIds,
    index,
  ]);

  const mainMenu = useMenu();

  const deleteDialog = useDialog<AuditLightingTypeFormEntry>();
  const isDeleteDisabled =
    updateMutationState.state === 'pending' ||
    loadAuditFormState.state !== 'success' ||
    loadAuditFormState.data.form == null ||
    index < 0;

  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (isDeleteDisabled || isNil(auditFormQuery.data)) {
        logger?.debug(
          'Cannot delete lighting entry, page is not in success state',
        );
        return;
      }

      mainMenu.close();

      if (!confirmed) {
        logger?.debug('Opening lighting entry deletion confirmation dialog');
        deleteDialog.open(loadAuditFormState.data!.form!);
        return;
      }

      const updatedData = { ...auditFormQuery.data };
      updatedData.entries.splice(index, 1);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: updatedData },
        {
          onSuccess: (auditForm) => {
            logger?.debug(
              { auditId, auditForm },
              'Lighting entry deleted successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [AUDIT_FORMS_QUERY_KEY, auditId, updatedData.type],
              })
              .then(() => {
                logger?.debug('Lighting list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate lighting list queries',
                );
                throw err;
              });
          },
          onError: (err) => {
            logger?.error({ err, auditId }, 'Failed to delete lighting entry');
            throw err;
          },
        },
      );
    },
    [auditId, loadAuditFormState, saveAuditFormMutation],
  );

  // sync form default values when data is loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { lightingTypeEntry: auditFormQuery.data?.entries[index] },
      'Lighting type form entry default values',
    );

    form.reset(
      toFormData(loadAuditFormState.data.form, loadAuditFormState.data.files),
    );
  }, [auditFormQuery.data?.entries, auditFormQuery.isSuccess]);

  return {
    mainMenu,

    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    onDelete,
    deleteDialog,
    isDeleteDisabled,

    auditId,
  };
};
